import { type ReactNode, type Ref, useState } from 'react'
import { useHoverState } from '@/hooks/element-hooks'
import { SplitView, SplitViewMain, SplitViewSide } from '@/components/SplitView'
import { SplitViewToggleButton } from '@/components/SplitView/SplitViewToggleButton'
import { Icon } from '@/components/Icon'
import styles from './Admin.module.css'
import { isRefreshDesign } from '@/infra/design'

export interface AdminProps {
  isNavVisible: boolean
  onToggleNav?: () => unknown
  nav: ReactNode
  /**
   * The admin page routes.
   */
  children?: ReactNode
}

export function Admin({
  isNavVisible,
  onToggleNav,
  nav,
  children,
}: AdminProps) {
  const [highlightBorder, setHighlightBorder] = useState(false)

  const { isHovering: isToggleHovering } = useHoverState(setHighlightBorder)

  return (
    <SplitView>
      {/* The refreshed global sidebar already lists every admin page. */}
      {!isRefreshDesign && (
        <SplitViewSide
          collapsed={!isNavVisible}
          highlightBorder={highlightBorder}
        >
          {nav}
        </SplitViewSide>
      )}
      <SplitViewMain>
        {!isRefreshDesign && (
          <div className={styles.toggleButtonContainer}>
            <AdminNavToggleButton
              onAdminNavSidebarToggle={onToggleNav}
              isAdminNavSidebarVisible={isNavVisible}
              isToggleHovering={isToggleHovering}
            />
          </div>
        )}
        {children}
      </SplitViewMain>
    </SplitView>
  )
}

interface AdminNavToggleButtonProps {
  onAdminNavSidebarToggle?: () => unknown
  isAdminNavSidebarVisible: boolean
  isToggleHovering: boolean
  toggleRef?: Ref<HTMLButtonElement>
}

function AdminNavToggleButton({
  onAdminNavSidebarToggle,
  isAdminNavSidebarVisible,
  isToggleHovering,
  toggleRef,
}: AdminNavToggleButtonProps) {
  if (onAdminNavSidebarToggle) {
    return (
      <SplitViewToggleButton onPress={onAdminNavSidebarToggle} ref={toggleRef}>
        {isToggleHovering ? (
          isAdminNavSidebarVisible ? (
            <Icon name="hover-arrow-highlight" />
          ) : (
            <Icon name="hover-menu-highlight" />
          )
        ) : isAdminNavSidebarVisible ? (
          <Icon name="hover-arrow" />
        ) : (
          <Icon name="hover-menu" />
        )}
      </SplitViewToggleButton>
    )
  }
  return null
}
